import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { can } from '../../utils/permissions';
import useLiveChannel from '../../hooks/useLiveChannel';
import useChatPush from '../../hooks/useChatPush';
import { SAFETY_NET_MS } from '../../utils/live/wsTransport';
import { createChatOutbox } from '../../utils/chat/outbox';
import ChannelList from './ChannelList';
import Conversation from './Conversation';
import NewConversationDialog from './NewConversationDialog';
import * as S from '../specimens/styles';

/*
 * My conversations beside the one open (2026-10-21, taken out of pages/Chat.js
 * so the Field camera's messages sheet is the same hub; docs/CHAT.md). Where
 * the open conversation is kept is the host's: the page keeps it in its
 * address (?c=, so a link opens it), the sheet in its own state. `narrow`
 * shows one or the other, the conversation with a way back to the list.
 *
 * The list is asked again every 15 seconds while it is shown, the open
 * conversation every few (Conversation.js); while the chat's socket is open
 * (utils/live/wsTransport.js, phase D2) a poke asks at once and the polling
 * slows to a five-minute safety net. Nothing for an account without
 * chat:read: the host decides what to show instead. `listHeadingAs` is the
 * list's heading level (ChannelList's `headingAs`): the page's h1 by default.
 */
export default function ChatHub({ user, selected = '', onSelect, narrow = false, style, testId, listHeadingAs }) {
  const { t } = useTranslation(['chat', 'common']);
  const [channels, setChannels] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const outbox = useMemo(() => createChatOutbox(), []);
  const allowed = can(user, 'chat:read');

  const pull = useCallback(() => api.get('/chat/channels').then((r) => r.data), []);
  const pokeRef = useRef(null);
  const live = useChatPush({ enabled: allowed, onPoke: () => pokeRef.current?.() });
  const poke = useLiveChannel(pull, (data) => setChannels(data?.channels || []),
    { enabled: allowed, base: live ? SAFETY_NET_MS : 15000, ...(live ? { max: SAFETY_NET_MS } : {}) });
  pokeRef.current = poke;

  if (!allowed) return null;
  const open = (id) => onSelect?.(id || '');

  const list = (
    <ChannelList channels={channels} selected={selected} onSelect={open} onNew={() => setNewOpen(true)}
      canStart={can(user, 'chat:use')} headingAs={listHeadingAs} />
  );
  const conversation = selected ? (
    <Conversation key={selected} channelId={selected} me={user?.user_id} outbox={outbox}
      onBack={narrow ? () => open('') : null} onLeft={() => { open(''); poke(); }} onChanged={poke} />
  ) : (
    <div style={{ padding: 24, ...S.hint }}>{t('pick')}</div>
  );

  return (
    <div data-testid={testId} style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'minmax(240px, 320px) 1fr',
      minHeight: 0, ...style }}>
      {(!narrow || !selected) && (
        <div style={{ borderInlineEnd: narrow ? 'none' : '1px solid var(--c-border)', minHeight: 0 }}>{list}</div>
      )}
      {(!narrow || selected) && <div style={{ minHeight: 0, minWidth: 0 }}>{conversation}</div>}
      {newOpen && (
        <NewConversationDialog onClose={() => setNewOpen(false)}
          onOpened={(id) => { setNewOpen(false); open(id); poke(); }} />
      )}
    </div>
  );
}
